import { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { AppRouter } from './components/Router';
import { AuthPage } from './components/AuthPage';
import { SbpGate } from './components/SbpGate';
import { AccessRestrictedGate } from './components/AccessRestrictedGate';
import { LinkTelegramPage } from './components/LinkTelegramPage';
import { useTelegram } from './hooks/useTelegram';
import { useAuth } from './hooks/useAuth';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { apiClient } from './services/api';
import type { OnboardingStatus } from './types';
import { isMaxWebApp, isTelegramWebApp } from './utils/platform';
import './App.css';
import { ScrollToTop } from './components/ScrollToTop';

function MainApp() {
  const { tg, isReady, colorScheme } = useTelegram();
  const { isLoading: isAuthLoading, isAuthenticated, user, pendingActor, retry: retryAuth } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showShortOnboarding, setShowShortOnboarding] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const startParamHandled = useRef(false);

  // First-login Max users with no users row yet are prompted to link an existing
  // Telegram-based account by phone (shown after onboarding, before SBP gate).
  const showLinkTelegram = useMemo(
    () =>
      !!pendingActor &&
      pendingActor.platform === 'max' &&
      !pendingActor.tg_link_prompt_dismissed,
    [pendingActor]
  );

  // Check onboarding status when Telegram is ready and user is authenticated
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!isReady || !isAuthenticated) return;

      try {
        const status = await apiClient.getOnboardingStatus();
        // Share the fetched status app-wide so pages (e.g. HomePage) don't refetch it.
        setOnboardingStatus(status);
        // SBP привязан или пользователь уже в white-list — без онбординга (в т.ч. короткого)
        if (status.sbp_completed || status.in_white_list) {
          setShowOnboarding(false);
          setShowShortOnboarding(false);
        } else if (!status.onboarding_completed) {
          // SBP not completed AND onboarding not completed -> show full onboarding
          setShowOnboarding(true);
          setShowShortOnboarding(false);
        } else {
          // SBP not completed BUT onboarding completed -> show short onboarding
          setShowOnboarding(false);
          setShowShortOnboarding(true);
        }
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Default to showing onboarding if we can't check
        // This ensures new users still see onboarding even if API is temporarily unavailable
        setShowOnboarding(true);
        setShowShortOnboarding(false);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [isReady, isAuthenticated]);

  // Admin analytics: one launch per tab after auth + first shell ready (onboarding or main).
  useEffect(() => {
    if (!isReady || !isAuthenticated || isAuthLoading || loading) {
      return;
    }
    if (sessionStorage.getItem('miniapp_launch_tracked') === '1') {
      return;
    }
    sessionStorage.setItem('miniapp_launch_tracked', '1');
    void apiClient.trackMiniappOpen().catch(() => {});
  }, [isReady, isAuthenticated, isAuthLoading, loading]);

  // Deep link: open a specific screen when launched via startapp. Supports
  // `ticket_<id>` (support ticket) and `nft_<id>` (NFT card). Runs once, only
  // when the main app shell is active (not during onboarding / Telegram-link gate).
  useEffect(() => {
    if (startParamHandled.current) return;
    if (!isReady || !isAuthenticated || isAuthLoading || loading) return;
    if (showOnboarding || showShortOnboarding || showLinkTelegram) return;

    const startParam = tg?.initDataUnsafe?.start_param;
    if (!startParam) return;

    const ticketMatch = /^ticket_(\d+)$/.exec(startParam);
    if (ticketMatch) {
      startParamHandled.current = true;
      navigate(`/support?ticket=${ticketMatch[1]}`);
      return;
    }

    const nftMatch = /^nft_(.+)$/.exec(startParam);
    if (nftMatch) {
      startParamHandled.current = true;
      navigate(`/nft/${nftMatch[1]}`);
    }
  }, [
    isReady,
    isAuthenticated,
    isAuthLoading,
    loading,
    showOnboarding,
    showShortOnboarding,
    showLinkTelegram,
    tg,
    navigate,
  ]);

  // Apply Telegram theme
  useEffect(() => {
    if (tg?.themeParams) {
      const root = document.documentElement;
      const theme = tg.themeParams;
      
      if (theme.bg_color) {
        root.style.setProperty('--tg-theme-bg-color', theme.bg_color);
      }
      if (theme.text_color) {
        root.style.setProperty('--tg-theme-text-color', theme.text_color);
      }
      if (theme.hint_color) {
        root.style.setProperty('--tg-theme-hint-color', theme.hint_color);
      }
      if (theme.button_color) {
        root.style.setProperty('--tg-theme-button-color', theme.button_color);
      }
      if (theme.button_text_color) {
        root.style.setProperty('--tg-theme-button-text-color', theme.button_text_color);
      }
    }

    // Apply color scheme class
    document.body.className = colorScheme === 'dark' ? 'dark-theme' : 'light-theme';
  }, [tg, colorScheme]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleShortOnboardingComplete = () => {
    setShowShortOnboarding(false);
  };

  const handleShowFullOnboarding = () => {
    setShowShortOnboarding(false);
    setShowOnboarding(true);
  };

  // Render content based on auth state
  const renderContent = () => {
    // Show loading screen while initializing Telegram or checking authentication
    if (!isReady || isAuthLoading) {
      return (
        <div className="app-loading">
          <div className="loading-content">
            <div className="loading-icon"></div>
            <p>Загрузка...</p>
          </div>
        </div>
      );
    }

    // If not authenticated: redirect to auth page (desktop) or show error (WebApp)
    if (!isAuthenticated) {
      const inMessengerWebApp = isTelegramWebApp() || isMaxWebApp();
      if (!inMessengerWebApp) {
        window.location.href = '/auth/telegram';
        return (
          <div className="app-loading">
            <div className="loading-content">
              <div className="loading-icon"></div>
              <p>Перенаправление...</p>
            </div>
          </div>
        );
      }
      return (
        <div className="app-loading">
          <div className="loading-content">
            <p>Требуется авторизация</p>
            <button onClick={retryAuth} disabled={isAuthLoading}>
              Повторить
            </button>
          </div>
        </div>
      );
    }

    // Show loading screen while checking onboarding
    if (loading) {
      return (
        <div className="app-loading">
          <div className="loading-content">
            <div className="loading-icon"></div>
            <p>Загрузка...</p>
          </div>
        </div>
      );
    }

    // Gate the main app for first-login Max users who haven't chosen whether to
    // link an existing Telegram account yet. Shown AFTER onboarding — while
    // OnboardingPage / ShortOnboardingPage are active, AppRouter owns the screen
    // and this gate does not interfere.
    if (showLinkTelegram && !showOnboarding && !showShortOnboarding) {
      return (
        <UserProvider user={user} pendingActor={pendingActor} isLoading={isAuthLoading || loading} onboardingStatus={onboardingStatus}>
          <NotificationProvider>
            <LinkTelegramPage
              onDismissed={retryAuth}
              onLinked={retryAuth}
            />
          </NotificationProvider>
        </UserProvider>
      );
    }

    // Main app content
    return (
      <UserProvider user={user} pendingActor={pendingActor} isLoading={isAuthLoading || loading} onboardingStatus={onboardingStatus}>
        <NotificationProvider>
          <SbpGate />
          <AppRouter 
            showOnboarding={showOnboarding}
            onOnboardingComplete={handleOnboardingComplete}
            showShortOnboarding={showShortOnboarding}
            onShortOnboardingComplete={handleShortOnboardingComplete}
            onShowFullOnboarding={handleShowFullOnboarding}
          />
        </NotificationProvider>
      </UserProvider>
    );
  };

  return renderContent();
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AccessRestrictedGate />
      <Routes>
        <Route path="/auth/telegram" element={<AuthPage />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
